/** @file Components that renders a visually-hidden <output> elements to render status updates to users using assitive technologies. */
import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import * as utils from '@/utils';
const clsAccessibleElement = [
    // Comment this out to visually debug status message.
    // Keep the comment to avoid accidentally commit this change.
    'sr-only',
];
const commonProps = {
    /** Relate this region to a or many elements. */
    for: p(String).required,
    /**
     * Specify how much delay should be added when updating the live region.
     *
     * This is also applied to the initial content.
     */
    delay: p(Number).default(500),
};
/** Name of the accessible status component. */
export const ACCESSIBLE_STATUS = 'accessible-status';
/**
 * The AccessibleStatus component is useful when you want to
 * prepare a live region for a piece of dynamic content.
 *
 * Use this component to describe the current status of a
 * dynamic region to the users using assitive technologies.
 * Users will also be notified of changes of the status.
 *
 * See: https://www.w3.org/WAI/WCAG21/Techniques/aria/ARIA22.
 */
export const AccessibleStatus = tsx.component({
    name: ACCESSIBLE_STATUS,
    props: {
        ...commonProps,
        /** The content of the status. */
        content: p.ofType().required,
    },
    /** @returns Local state of the component. */
    data() {
        return { currentContent: utils.emptyVNode() };
    },
    watch: {
        content: {
            immediate: true,
            /** Update currentContent with the new content with a delay.  */
            handler() {
                setTimeout(() => {
                    this.currentContent = this.content;
                }, this.delay);
            },
        },
    },
    /** @returns The output node. */
    render() {
        return (<output for={this.for} class={clsAccessibleElement} aria-live="polite" aria-atomic={true} data-cy={ACCESSIBLE_STATUS}>
                {this.currentContent}
            </output>);
    },
});
/** Name of the AccessibleLogs component. */
export const ACCESSIBLE_LOGS = 'accessible-logs';
/**
 * The AccessibleLogs component is useful when you want to
 * prepare a live region that has sequential information updates.
 *
 * Example usage: When you want to present a series of events where the
 * sequence of events is important and must be made clear to users using
 * assistive technologies.
 *
 * See: https://www.w3.org/WAI/WCAG21/Techniques/aria/ARIA23.
 */
export const AccessibleLogs = tsx.component({
    name: ACCESSIBLE_LOGS,
    props: {
        ...commonProps,
        title: p(String).required,
        logs: p.ofArray().required,
    },
    /** @returns Local state of the component. */
    data() {
        return { currentTitle: null, currentLogs: [] };
    },
    watch: {
        title: {
            immediate: true,
            /** Update currentTitle with a delay. */
            handler() {
                setTimeout(() => {
                    this.currentTitle = this.title;
                }, this.delay);
            },
        },
        logs: {
            immediate: true,
            /** Update currentLogs with a delay. */
            handler() {
                setTimeout(() => {
                    this.currentLogs = [...this.logs];
                }, this.delay);
            },
        },
    },
    computed: {
        /** @returns The node for the title. */
        titleNode() {
            if (this.currentTitle === null) {
                return utils.emptyVNode();
            }
            return <h5 class="status-title">{this.currentTitle}</h5>;
        },
        /** @returns The node for the logs. */
        logsNode() {
            if (this.currentLogs.length === 0) {
                return utils.emptyVNode();
            }
            return (<ul class="log-entries list-none">
                    {this.currentLogs.map(entry => (<li>{entry}</li>))}
                </ul>);
        },
    },
    /** @returns The output node. */
    render() {
        return (<output for={this.for} class={clsAccessibleElement} role="log" aria-live="polite" aria-atomic={false} aria-relevant="additions" data-cy={ACCESSIBLE_LOGS}>
                {this.titleNode}
                {this.logsNode}
            </output>);
    },
});
